import { useRef, useState } from 'react';
import classNames from 'clsx';

type SimpleOTPInputProps = {
  value: string;
  onChange: (val: string) => void;
  maxLength?: number;
  mask?: boolean;
  className?: string;
  slotClassName?: string;
};

export const SimpleOTPInput = ({
  value,
  onChange,
  maxLength = 6,
  mask = false,
  className,
  slotClassName
}: SimpleOTPInputProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const digits = (value || '').split('').concat(Array(maxLength).fill('')).slice(0, maxLength);

  const handleChange = (index: number, digit: string) => {
    if (!/^\d*$/.test(digit)) return;
    const newDigits = [...digits];
    newDigits[index] = digit.slice(-1);
    onChange(newDigits.join(''));
    if (digit && index < maxLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Allow arrow key navigation
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < maxLength - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, maxLength);
    if (pastedData) {
      onChange(pastedData);
      const focusIndex = Math.min(pastedData.length, maxLength - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
    // Select content on focus for easy replacement
    inputRefs.current[index]?.select();
  };

  const handleBlur = () => {
    setFocusedIndex(null);
  };

  return (
    <div className={classNames('flex items-center justify-center', className)}>
      <div className="flex items-center gap-2 sm:gap-3">
        {digits.map((digit, i) => {
          const isFocused = focusedIndex === i;
          const isFilled = digit !== '';

          return (
            <div key={i} className="relative">
              {/* Glow effect for focused state */}
              <div
                className={classNames(
                  'absolute -inset-1 rounded-lg transition-[background-color,opacity,filter] duration-300',
                  isFocused ? 'bg-primary/20 blur-md opacity-100' : 'opacity-0'
                )}
              />

              <input
                ref={(el) => {
                  inputRefs.current[i] = el;
                }}
                type={mask ? 'password' : 'text'}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                onFocus={() => handleFocus(i)}
                onBlur={handleBlur}
                aria-label={`Digit ${i + 1} of ${maxLength}`}
                className={classNames(
                  // Base styles
                  'relative w-11 h-14 sm:w-12 sm:h-14',
                  'text-center text-xl sm:text-2xl font-semibold',
                  'rounded-lg border-2 outline-hidden',
                  'transition-[color,background-color,border-color,box-shadow,scale] duration-200 ease-out',
                  // Colors and states
                  isFocused
                    ? 'border-frost-brand bg-frost-card shadow-frost-md scale-105 text-frost-ink'
                    : isFilled
                      ? 'border-frost-hair-stronger bg-frost-paper text-frost-ink'
                      : 'border-frost-hair-strong bg-frost-card text-frost-ink hover:border-frost-hair-stronger',
                  // Selection styles
                  'selection:bg-frost-brand/20',
                  // Caret styles
                  'caret-frost-brand',
                  slotClassName
                )}
                style={{
                  // Hide default password dots for mask mode, show custom
                  ...(mask && digit
                    ? {
                        fontSize: '2rem',
                        letterSpacing: '0'
                      }
                    : {})
                }}
              />

              {/* Cursor indicator for empty focused slot */}
              {isFocused && !digit && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-0.5 h-6 bg-primary rounded-full animate-pulse" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
